import { useMemo } from 'react';
import { useTasks } from './useTasks';
import { useMilestones } from './useMilestones';
import { leavesForDomain, type DomainConfig } from './useDomains';
import type { Task } from '../types/task';

export type DomainStatus = 'green' | 'yellow' | 'red';

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function computeStatus(dueDates: (string | undefined)[]): DomainStatus {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const sevenDays = new Date(now);
  sevenDays.setDate(sevenDays.getDate() + 7);
  const fourteenDays = new Date(now);
  fourteenDays.setDate(fourteenDays.getDate() + 14);

  let hasYellow = false;

  for (const d of dueDates) {
    if (!d) continue;
    const due = parseLocalDate(d);
    if (due.getTime() <= sevenDays.getTime()) return 'red';
    if (due.getTime() <= fourteenDays.getTime()) hasYellow = true;
  }

  return hasYellow ? 'yellow' : 'green';
}

export function useDomainStatuses(domains: DomainConfig[]): Record<string, DomainStatus> {
  const { data: tasks } = useTasks();
  const { data: milestones } = useMilestones({ year: 'all' });

  return useMemo(() => {
    const statuses: Record<string, DomainStatus> = {};
    if (!tasks) return statuses;

    for (const domain of domains) {
      // A container aggregates its leaf domains; a leaf resolves to itself.
      const leafSet = new Set(leavesForDomain(domain.id, domains));

      // Exclude milestones from the task pass — they're counted via the milestone
      // pass below. GET /tasks returns milestones too (with relative due dates
      // resolved), so without this guard a milestone would inflate the sidebar
      // dot while never appearing in DomainTasksList (which also excludes
      // milestones from its task list). Keeps the dot consistent with the page.
      const incompleteTasks = tasks.filter(
        (t: Task) =>
          t.status !== 'done' &&
          t.type !== 'milestone' &&
          !!t.domain &&
          leafSet.has(t.domain),
      );

      const incompleteMilestones = (milestones ?? []).filter((m: Task) => {
        if (m.status === 'done') return false;
        const mDomains = m.domains ?? (m.domain ? [m.domain] : []);
        return mDomains.some((d) => leafSet.has(d));
      });

      const dueDates = [
        ...incompleteTasks.map((t) => t.dueDate),
        ...incompleteMilestones.map((m) => m.dueDate),
      ];

      statuses[domain.id] = computeStatus(dueDates);
    }

    return statuses;
  }, [tasks, milestones, domains]);
}
